/**
 * SIA Wishlist Engine
 * ─────────────────────────────────────────────────────────────
 * • localStorage-based, zero external dependencies
 * • Works across all Shopify sections (product cards, product
 *   pages, quick-add modals, featured collections, etc.)
 * • Dispatches CustomEvents so any part of the theme can react
 *
 * Public API (all methods are on window.SIAWishlist):
 *   .add(productId, productData)
 *   .remove(productId)
 *   .toggle(productId, productData)
 *   .has(productId)
 *   .getAll()          → Array of saved product objects
 *   .count()           → Integer
 *   .openDrawer()
 *   .closeDrawer()
 *
 * ProductData shape expected by .add():
 *   { id, title, handle, price, image, url, variant_id }
 * ─────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'sia_wishlist';
  const DRAWER_ID   = 'sia-wishlist-drawer';

  /* ─── Storage helpers ─────────────────────────────────────── */
  function read() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch { return []; }
  }
  function write(items) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }
    catch { /* storage full — fail silently */ }
  }

  /* ─── Core wishlist object ────────────────────────────────── */
  const SIAWishlist = {
    add(productId, productData) {
      const items = read();
      const id    = String(productId);
      if (!items.find(i => String(i.id) === id)) {
        items.push({ ...productData, id });
        write(items);
        this._emit('wishlist:add', { productId: id, item: productData });
        this._emit('wishlist:change', { count: items.length });
      }
      this._syncButtons(id, true);
      this._updateBadge();
    },

    remove(productId) {
      const id    = String(productId);
      const items = read().filter(i => String(i.id) !== id);
      write(items);
      this._emit('wishlist:remove', { productId: id });
      this._emit('wishlist:change', { count: items.length });
      this._syncButtons(id, false);
      this._updateBadge();
      this._renderDrawerItems();   // live-update open drawer
    },

    toggle(productId, productData) {
      this.has(productId)
        ? this.remove(productId)
        : this.add(productId, productData);
    },

    has(productId) {
      return !!read().find(i => String(i.id) === String(productId));
    },

    getAll() { return read(); },
    count()  { return read().length; },

    openDrawer() {
      const drawer = document.getElementById(DRAWER_ID);
      if (!drawer) return;
      this._renderDrawerItems();
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
      document.body.classList.add('sia-wishlist-open');
      // focus trap entry
      const first = drawer.querySelector('button, a, [tabindex="0"]');
      if (first) setTimeout(() => first.focus(), 100);
    },

    closeDrawer() {
      const drawer = document.getElementById(DRAWER_ID);
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('is-open');
      document.body.classList.remove('sia-wishlist-open');
    },

    /* ─── Internal helpers ──────────────────────────────────── */

    _emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    },

    _updateBadge() {
      const count   = this.count();
      const badges  = document.querySelectorAll('.sia-wishlist-badge');
      badges.forEach(el => {
        el.textContent = count > 99 ? '99+' : String(count);
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    },

    /* Set all buttons for a given product to active/inactive */
    _syncButtons(productId, isAdded) {
      const id  = String(productId);
      const btns = document.querySelectorAll(`[data-wishlist-id="${id}"]`);
      btns.forEach(btn => {
        btn.classList.toggle('is-wishlisted', isAdded);
        btn.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
        const label = btn.querySelector('.sia-wishlist-btn-label');
        if (label) {
          label.textContent = isAdded ? 'Wishlisted' : 'Wishlist';
        }
        const icon = btn.querySelector('.sia-wl-heart');
        if (icon) {
          icon.setAttribute('fill', isAdded ? '#a93226' : 'none');
          icon.setAttribute('stroke', isAdded ? '#a93226' : 'currentColor');
        }
      });
    },

    /* Re-render the items list inside the open drawer */
    _renderDrawerItems() {
      const container = document.getElementById('sia-wishlist-items');
      if (!container) return;
      const items = this.getAll();

      if (items.length === 0) {
        container.innerHTML = `
          <div class="sia-wl-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c4b5a5" stroke-width="1.2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            <p>Your wishlist is empty</p>
            <span>Save items you love — they'll appear here.</span>
          </div>`;
        return;
      }

      container.innerHTML = items.map(item => `
        <div class="sia-wl-item" data-item-id="${item.id}">
          <a href="${item.url || '/products/' + item.handle}" class="sia-wl-item__img-wrap">
            ${item.image
              ? `<img src="${item.image}" alt="${_esc(item.title)}" loading="lazy" width="80" height="80">`
              : `<div class="sia-wl-item__img-placeholder"></div>`}
          </a>
          <div class="sia-wl-item__info">
            <a href="${item.url || '/products/' + item.handle}" class="sia-wl-item__title">${_esc(item.title)}</a>
            <span class="sia-wl-item__price">${_formatPrice(item.price)}</span>
            <div class="sia-wl-item__actions">
              <a href="${item.url || '/products/' + item.handle}" class="sia-wl-item__cta">View Product</a>
              <button
                type="button"
                class="sia-wl-item__remove"
                data-remove-id="${item.id}"
                aria-label="Remove ${_esc(item.title)} from wishlist"
              >Remove</button>
            </div>
          </div>
        </div>`).join('');

      // Wire up remove buttons inside the freshly-rendered list
      container.querySelectorAll('[data-remove-id]').forEach(btn => {
        btn.addEventListener('click', () => this.remove(btn.dataset.removeId));
      });
    },
  };

  /* ─── Utility helpers ─────────────────────────────────────── */
  function _esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function _formatPrice(price) {
    // price arrives as integer cents from Shopify (e.g. 299900 = ₹2,999)
    if (!price && price !== 0) return '';
    const num = parseInt(price, 10);
    if (isNaN(num)) return String(price);
    return '₹' + (num / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  /* ─── Expose globally ─────────────────────────────────────── */
  window.SIAWishlist = SIAWishlist;

  /* ─── Boot: sync all buttons & badges on page load ───────── */
  document.addEventListener('DOMContentLoaded', () => {
    // Sync any wishlist buttons that were rendered server-side
    const items = read();
    items.forEach(item => SIAWishlist._syncButtons(item.id, true));
    SIAWishlist._updateBadge();
  });

  /* ─── Event delegation for wishlist toggle buttons ───────── */
  document.addEventListener('click', function (e) {
    // ── Toggle button ──
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (btn) {
      e.preventDefault();
      const productId  = btn.dataset.wishlistId   || btn.dataset.wishlistToggle;
      const title      = btn.dataset.wishlistTitle  || '';
      const handle     = btn.dataset.wishlistHandle || '';
      const price      = btn.dataset.wishlistPrice  || 0;
      const image      = btn.dataset.wishlistImage  || '';
      const url        = btn.dataset.wishlistUrl    || '/products/' + handle;
      const variantId  = btn.dataset.wishlistVariant || '';

      SIAWishlist.toggle(productId, { id: productId, title, handle, price, image, url, variant_id: variantId });

      // Toast notification
      const wasAdded = SIAWishlist.has(productId);
      _showToast(wasAdded ? '♥ Added to wishlist' : 'Removed from wishlist', wasAdded ? 'add' : 'remove');
      return;
    }

    // ── Open drawer (header icon / any element with data-wishlist-open) ──
    const opener = e.target.closest('[data-wishlist-open]');
    if (opener) {
      e.preventDefault();
      SIAWishlist.openDrawer();
      return;
    }

    // ── Overlay backdrop click → close ──
    if (e.target.id === 'sia-wishlist-overlay') {
      SIAWishlist.closeDrawer();
    }
  });

  // Keyboard: Escape → close drawer
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') SIAWishlist.closeDrawer();
  });

  /* ─── Toast helper ────────────────────────────────────────── */
  function _showToast(message, type = 'add') {
    let toast = document.getElementById('sia-wl-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'sia-wl-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.className   = 'sia-wl-toast sia-wl-toast--' + type + ' is-visible';
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 2800);
  }

})();